import {
  convertToImperialNoIntlFormat,
  convertToMetricNoIntlFormat,
} from '@innovyze/stylovyze';

export const LENGTH_COST_PER = 'length';

export function isLengthBasedCostPer(costPer?: string): boolean {
  return costPer === LENGTH_COST_PER;
}

/**
 * Rounds a value to cent precision (2 decimal places).
 *
 * `toPrecision(15)` strips the IEEE-754 representation noise that appears
 * past the 15th significant digit (e.g. 1.005 * 100 = 100.49999999999999)
 * before rounding, so x.xx5 boundaries round consistently. Matches
 * roundToCents in costCalculations.ts.
 */
function roundToTwoDecimalPlaces(value: number): number {
  return Math.round(Number((value * 100).toPrecision(15))) / 100;
}

/** Round canonical $/m from CurrencyField / inline edit (already metric). */
export function normalizeStoredLengthUnitCostPerMeter(value: number): number {
  return roundToTwoDecimalPlaces(value);
}

/** Tenant display input from CurrencyField → canonical $/m for length-based save. */
export function normalizeLengthUnitCostFromUserInput(
  userValue: number,
  system: 'Metric' | 'Imperial',
  getSystemUnit: (unit: string) => string | undefined
): number {
  if (system === 'Metric') {
    return normalizeStoredLengthUnitCostPerMeter(userValue);
  }
  const displayUnit = getSystemUnit('cost/m') ?? 'cost/ft';
  const converted = convertToMetricNoIntlFormat(`${userValue} ${displayUnit}`);
  return roundToTwoDecimalPlaces(+converted.value);
}

/** Display value for client-side grid filter/sort (rows store canonical $/m). */
export function getUnitCostForClientGridOps(
  unitCost: number | null | undefined,
  costPer: string | undefined,
  system: 'Metric' | 'Imperial'
): number | null | undefined {
  if (!isLengthBasedCostPer(costPer)) {
    return unitCost;
  }
  return formatLengthUnitCostForDisplay(unitCost, system);
}

/** Display value for length-based unit cost from canonical $/m storage. */
export function formatLengthUnitCostForDisplay(
  canonicalPerMeter: number | null | undefined,
  system: 'Metric' | 'Imperial'
): number | null | undefined {
  if (canonicalPerMeter == null || Number.isNaN(canonicalPerMeter)) {
    return canonicalPerMeter;
  }
  if (system === 'Imperial') {
    const converted = convertToImperialNoIntlFormat(
      `${canonicalPerMeter} cost/m`
    );
    return roundToTwoDecimalPlaces(+converted.value);
  }
  return roundToTwoDecimalPlaces(canonicalPerMeter);
}

export function getLengthUnitCostColumnLabel(
  t: (key: string, params?: Record<string, string>) => string,
  system: 'Metric' | 'Imperial'
): string {
  return system === 'Imperial' ? t('Unit Cost ($/ft)') : t('Unit Cost ($/m)');
}

/** Cost-per option/display label for length-based tables (configuration, list, create). */
export function getLengthCostPerDisplayLabel(
  t: (key: string, params?: Record<string, string>) => string,
  system: 'Metric' | 'Imperial'
): string {
  return system === 'Imperial' ? t('/ft (length)') : t('/m (length)');
}

/** CSV export/import uses canonical $/m (API storage), not grid display units. */
export function getLengthUnitCostCsvColumnLabel(
  t: (key: string, params?: Record<string, string>) => string
): string {
  return t('Unit Cost ($/m)');
}

/** CSV upload row with a unit cost field to normalize. */
export type LengthUnitCostRow = {
  unitCost: number | null | undefined;
};

/**
 * Normalizes CSV upload updates for length-based tables. Exported CSV and
 * existingData both use canonical $/m — round only; do not Imperial-convert.
 */
export function normalizeCsvLengthUnitCostUpdates<
  T extends LengthUnitCostRow
>(updates: Record<string, T>): Record<string, T> {
  return Object.fromEntries(
    Object.entries(updates).map(([id, row]) => [
      id,
      {
        ...row,
        unitCost:
          row.unitCost == null || Number.isNaN(row.unitCost)
            ? row.unitCost
            : normalizeStoredLengthUnitCostPerMeter(row.unitCost),
      },
    ])
  ) as Record<string, T>;
}
